import React, { useState, useRef } from 'react';
import axios from 'axios';
import { 
  Plus, 
  Trash2, 
  GripVertical,
  FileText,
  Video,
  Image as ImageIcon,
  Type,
  List,
  ChevronDown,
  ChevronUp,
  Upload,
  Link,
  Play,
  File,
  Loader2,
  Sparkles,
  X,
  Wand2,
  Brain,
  Zap,
  Eye,
  CheckCircle,
  BookOpen,
  FlaskConical
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

// Content block types
const CONTENT_TYPES = [
  { value: 'text', label: 'Text Content', icon: Type },
  { value: 'video', label: 'Video', icon: Video },
  { value: 'image', label: 'Image', icon: ImageIcon },
  { value: 'document', label: 'Document', icon: FileText },
  { value: 'quiz', label: 'Quiz Question', icon: List },
  { value: 'embed', label: 'External Embed', icon: Link },
];

// Duration options for AI generation
const DURATION_OPTIONS = [
  "30 minutes",
  "1 hour", 
  "2 hours",
  "Half day (4 hours)",
  "1 day"
];

const NUM_SECTIONS_OPTIONS = [3, 4, 5, 6, 7, 8];

/**
 * ActivityContentBuilder - Inline content builder for programme activities
 * Similar to Course Builder but embedded within the Programme Builder
 * Includes AI Course Generation and Learning Lab creation
 */
export default function ActivityContentBuilder({ 
  activity, 
  onUpdate, 
  token,
  programmeId
}) {
  const [expanded, setExpanded] = useState(true);
  const [modules, setModules] = useState(activity.modules || []);
  const [showAddContent, setShowAddContent] = useState(null); // module_id or null
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // AI Generation state
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationMessage, setGenerationMessage] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [aiForm, setAiForm] = useState({
    overview: '',
    duration: '1 hour',
    num_sections: 5,
    content_type: 'course' // course or learning_lab
  });

  const headers = { Authorization: `Bearer ${token}` };

  // Generate Preview
  const handleGeneratePreview = async () => {
    if (!activity.title) {
      toast.error('Please enter an activity title first');
      return;
    }

    setPreviewLoading(true);
    setPreviewData(null);
    
    try {
      const response = await axios.post(`${API}/programme-ai/preview`, {
        title: activity.title,
        content_type: aiForm.content_type,
        overview: aiForm.overview,
        duration: aiForm.duration,
        num_sections: aiForm.num_sections
      }, { headers });

      setPreviewData(response.data.preview);
      setShowPreview(true);
    } catch (error) {
      console.error('Preview generation failed:', error);
      toast.error('Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  // AI Course Generation with Progress Streaming
  const handleAIGenerate = async () => {
    if (!activity.title) {
      toast.error('Please enter an activity title first');
      return;
    }

    setAiGenerating(true);
    setGenerationProgress(5);
    setGenerationMessage('Starting generation...');
    
    try {
      let response;
      let generationId;
      
      if (aiForm.content_type === 'learning_lab') {
        // Generate Learning Lab
        response = await axios.post(`${API}/programme-ai/generate-learning-lab`, {
          title: activity.title,
          description: aiForm.overview,
          duration_minutes: parseInt(aiForm.duration) || 60,
          num_scenarios: aiForm.num_sections
        }, { headers });
        
        generationId = response.data.generation_id;
      } else {
        // Generate Course
        response = await axios.post(`${API}/ai/generate-course`, {
          title: activity.title,
          overview: aiForm.overview || `Comprehensive content for ${activity.title}`,
          duration: aiForm.duration,
          num_sections: aiForm.num_sections,
          author_name: 'Cerebro Core',
          auto_generate_thumbnail: true,
          programme_activity: true
        }, { headers, timeout: 60000 });
        
        generationId = response.data.course_id;
      }

      // Start progress streaming
      if (generationId) {
        await streamProgress(generationId, aiForm.content_type);
        
        // Update activity with generated content
        onUpdate({
          ...activity,
          content_id: generationId,
          ai_generated: true,
          ai_course_id: aiForm.content_type === 'course' ? generationId : null,
          ai_lab_id: aiForm.content_type === 'learning_lab' ? generationId : null,
          content_type: aiForm.content_type
        });

        toast.success(`AI ${aiForm.content_type === 'learning_lab' ? 'Learning Lab' : 'Course'} generated successfully!`);
        setShowAIDialog(false);
        setShowPreview(false);
      }
    } catch (error) {
      console.error('AI generation failed:', error);
      toast.error('Failed to generate content');
    } finally {
      setAiGenerating(false);
      setGenerationProgress(0);
      setGenerationMessage('');
    }
  };

  // Stream progress updates via SSE
  const streamProgress = async (generationId, contentType) => {
    return new Promise((resolve, reject) => {
      // For courses, poll the course status
      if (contentType === 'course') {
        const pollInterval = setInterval(async () => {
          try {
            const res = await axios.get(`${API}/courses/${generationId}`, { headers });
            const course = res.data;
            
            const progress = course.generation_progress || 0;
            const message = course.generation_message || 'Generating...';
            
            setGenerationProgress(progress);
            setGenerationMessage(message);
            
            if (course.status === 'published' || course.status === 'draft' || progress >= 100) {
              clearInterval(pollInterval);
              resolve();
            }
          } catch (e) {
            // Continue polling
          }
        }, 2000);
        
        // Timeout after 2 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          resolve();
        }, 120000);
      } else {
        // For learning labs, use SSE streaming
        const eventSource = new EventSource(
          `${API}/programme-ai/generation/${generationId}/stream?token=${token}`
        );
        
        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            setGenerationProgress(data.progress || 0);
            setGenerationMessage(data.message || 'Generating...');
            
            if (data.status === 'completed' || data.status === 'failed' || data.status === 'stream_ended') {
              eventSource.close();
              resolve();
            }
          } catch (e) {
            console.error('Error parsing SSE data:', e);
          }
        };
        
        eventSource.onerror = () => {
          eventSource.close();
          resolve();
        };
        
        // Timeout after 2 minutes
        setTimeout(() => {
          eventSource.close();
          resolve();
        }, 120000);
      }
    });
  };

  // Update parent when modules change
  const updateModules = (newModules) => {
    setModules(newModules);
    onUpdate({ ...activity, modules: newModules });
  };

  // Add a new module
  const addModule = () => {
    const newModule = {
      module_id: `mod_${Date.now()}`,
      title: `Module ${modules.length + 1}`,
      description: '',
      sections: [],
      order: modules.length + 1,
      isNew: true
    };
    updateModules([...modules, newModule]);
  };

  // Update module
  const updateModule = (moduleId, field, value) => {
    updateModules(modules.map(m => 
      m.module_id === moduleId ? { ...m, [field]: value } : m
    ));
  };

  // Delete module
  const deleteModule = (moduleId) => {
    if (!window.confirm('Delete this module and all its content?')) return;
    updateModules(modules.filter(m => m.module_id !== moduleId));
  };

  // Add a section to a module
  const addSection = (moduleId) => {
    updateModules(modules.map(m => {
      if (m.module_id === moduleId) {
        const newSection = {
          section_id: `sec_${Date.now()}`,
          title: `Section ${(m.sections?.length || 0) + 1}`,
          content_blocks: [],
          order: (m.sections?.length || 0) + 1,
          isNew: true
        };
        return { ...m, sections: [...(m.sections || []), newSection] };
      }
      return m;
    }));
  };

  // Update section
  const updateSection = (moduleId, sectionId, field, value) => {
    updateModules(modules.map(m => {
      if (m.module_id === moduleId) {
        return {
          ...m,
          sections: m.sections.map(s =>
            s.section_id === sectionId ? { ...s, [field]: value } : s
          )
        };
      }
      return m;
    }));
  };

  // Delete section
  const deleteSection = (moduleId, sectionId) => {
    updateModules(modules.map(m => {
      if (m.module_id === moduleId) {
        return { ...m, sections: m.sections.filter(s => s.section_id !== sectionId) };
      }
      return m;
    }));
  };

  // Add content block to section
  const addContentBlock = (moduleId, sectionId, type) => {
    const newBlock = {
      block_id: `block_${Date.now()}`,
      type: type,
      content: '',
      title: '',
      url: '',
      order: 0,
      isNew: true
    };

    updateModules(modules.map(m => {
      if (m.module_id === moduleId) {
        return {
          ...m,
          sections: m.sections.map(s => {
            if (s.section_id === sectionId) {
              const blocks = s.content_blocks || [];
              newBlock.order = blocks.length + 1;
              return { ...s, content_blocks: [...blocks, newBlock] };
            }
            return s;
          })
        };
      }
      return m;
    }));

    setShowAddContent(null);
  };

  // Update content block
  const updateContentBlock = (moduleId, sectionId, blockId, field, value) => {
    updateModules(modules.map(m => {
      if (m.module_id === moduleId) {
        return {
          ...m,
          sections: m.sections.map(s => {
            if (s.section_id === sectionId) {
              return {
                ...s,
                content_blocks: s.content_blocks.map(b =>
                  b.block_id === blockId ? { ...b, [field]: value } : b
                )
              };
            }
            return s;
          })
        };
      }
      return m;
    }));
  };

  // Delete content block
  const deleteContentBlock = (moduleId, sectionId, blockId) => {
    updateModules(modules.map(m => {
      if (m.module_id === moduleId) {
        return {
          ...m,
          sections: m.sections.map(s => {
            if (s.section_id === sectionId) {
              return {
                ...s,
                content_blocks: s.content_blocks.filter(b => b.block_id !== blockId)
              };
            }
            return s;
          })
        };
      }
      return m;
    }));
  };

  // Handle file upload for content blocks
  const handleFileUpload = async (file, moduleId, sectionId, blockId, type) => {
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Upload to media library or programme documents
      const endpoint = type === 'document' 
        ? `${API}/programmes/${programmeId}/upload-content`
        : `${API}/media/upload`;

      const res = await axios.post(endpoint, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });

      const fileUrl = res.data.url || res.data.file_url || res.data.document_url;
      
      updateContentBlock(moduleId, sectionId, blockId, 'url', fileUrl);
      updateContentBlock(moduleId, sectionId, blockId, 'title', file.name);
      
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  // Render content block based on type
  const renderContentBlock = (block, moduleId, sectionId) => {
    const blockTypes = {
      text: (
        <div className="space-y-2">
          <Label className="text-zinc-400 text-xs">Text Content</Label>
          <Textarea
            value={block.content || ''}
            onChange={(e) => updateContentBlock(moduleId, sectionId, block.block_id, 'content', e.target.value)}
            placeholder="Enter your content here... (Supports basic formatting)"
            rows={4}
            className="bg-zinc-800 border-zinc-700 text-white text-sm"
          />
        </div>
      ),
      video: (
        <div className="space-y-3">
          <div>
            <Label className="text-zinc-400 text-xs">Video URL (YouTube, Vimeo, or direct link)</Label>
            <Input
              value={block.url || ''}
              onChange={(e) => updateContentBlock(moduleId, sectionId, block.block_id, 'url', e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className="bg-zinc-800 border-zinc-700 text-white text-sm mt-1"
            />
          </div>
          {block.url && (
            <div className="p-3 bg-zinc-800 rounded-lg flex items-center gap-2">
              <Play className="h-4 w-4 text-teal-400" />
              <span className="text-sm text-zinc-300 truncate">{block.url}</span>
            </div>
          )}
        </div>
      ),
      image: (
        <div className="space-y-3">
          <div>
            <Label className="text-zinc-400 text-xs">Image URL or Upload</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={block.url || ''}
                onChange={(e) => updateContentBlock(moduleId, sectionId, block.block_id, 'url', e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="bg-zinc-800 border-zinc-700 text-white text-sm flex-1"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/*';
                  input.onchange = (e) => handleFileUpload(e.target.files[0], moduleId, sectionId, block.block_id, 'image');
                  input.click();
                }}
                className="border-zinc-700 text-zinc-300"
                disabled={uploading}
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          {block.url && (
            <div className="max-h-32 overflow-hidden rounded-lg border border-zinc-700">
              <img src={block.url} alt="" className="w-full h-full object-cover" />
            </div>
          )}
        </div>
      ),
      document: (
        <div className="space-y-3">
          <div>
            <Label className="text-zinc-400 text-xs">Document (PDF, DOCX, PPTX)</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={block.title || ''}
                readOnly
                placeholder="No file selected"
                className="bg-zinc-800 border-zinc-700 text-white text-sm flex-1"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx';
                  input.onchange = (e) => handleFileUpload(e.target.files[0], moduleId, sectionId, block.block_id, 'document');
                  input.click();
                }}
                className="border-zinc-700 text-zinc-300"
                disabled={uploading}
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          {block.url && (
            <div className="p-3 bg-zinc-800 rounded-lg flex items-center gap-2">
              <File className="h-4 w-4 text-amber-400" />
              <span className="text-sm text-zinc-300 truncate">{block.title || block.url}</span>
            </div>
          )}
        </div>
      ),
      quiz: (
        <div className="space-y-3">
          <div>
            <Label className="text-zinc-400 text-xs">Question</Label>
            <Input
              value={block.content || ''}
              onChange={(e) => updateContentBlock(moduleId, sectionId, block.block_id, 'content', e.target.value)}
              placeholder="Enter your quiz question"
              className="bg-zinc-800 border-zinc-700 text-white text-sm mt-1"
            />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Answer Options (one per line)</Label>
            <Textarea
              value={block.options || ''}
              onChange={(e) => updateContentBlock(moduleId, sectionId, block.block_id, 'options', e.target.value)}
              placeholder="Option A&#10;Option B&#10;Option C&#10;Option D"
              rows={4}
              className="bg-zinc-800 border-zinc-700 text-white text-sm mt-1"
            />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Correct Answer (e.g., A, B, C, or D)</Label>
            <Input
              value={block.correct_answer || ''}
              onChange={(e) => updateContentBlock(moduleId, sectionId, block.block_id, 'correct_answer', e.target.value)}
              placeholder="A"
              className="bg-zinc-800 border-zinc-700 text-white text-sm mt-1 w-20"
            />
          </div>
        </div>
      ),
      embed: (
        <div className="space-y-2">
          <Label className="text-zinc-400 text-xs">External Embed URL (SCORM, AICC, iframe)</Label>
          <Input
            value={block.url || ''}
            onChange={(e) => updateContentBlock(moduleId, sectionId, block.block_id, 'url', e.target.value)}
            placeholder="https://external-lms.com/content/..."
            className="bg-zinc-800 border-zinc-700 text-white text-sm"
          />
        </div>
      )
    };

    return blockTypes[block.type] || null;
  };

  // Get icon for content type
  const getContentIcon = (type) => {
    const icons = {
      text: Type,
      video: Video,
      image: ImageIcon,
      document: FileText,
      quiz: List,
      embed: Link
    };
    const Icon = icons[type] || FileText;
    return <Icon className="h-4 w-4" />;
  };

  return (
    <div className="mt-4 border-t border-zinc-700 pt-4">
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between cursor-pointer hover:bg-zinc-800/50 p-2 rounded-lg -mx-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-400" />
              <span className="text-sm font-medium text-white">Course Content Builder</span>
              <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-400">
                {modules.length} module{modules.length !== 1 ? 's' : ''}
              </Badge>
            </div>
            {expanded ? (
              <ChevronUp className="h-4 w-4 text-zinc-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-zinc-400" />
            )}
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent className="mt-4 space-y-4">
          {/* AI Generation & Manual Build Options */}
          {modules.length === 0 && !activity.ai_generated && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              {/* AI Generation Option */}
              <div 
                onClick={() => setShowAIDialog(true)}
                className="p-4 bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-lg border border-purple-500/30 cursor-pointer hover:border-purple-500/50 transition-colors"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-purple-500/20">
                    <Brain className="h-5 w-5 text-purple-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Generate with NAVI AI</h4>
                    <p className="text-xs text-zinc-400">Auto-create course content</p>
                  </div>
                </div>
                <p className="text-xs text-zinc-500">
                  NAVI will generate modules, sections, and quiz questions based on your activity title.
                </p>
              </div>

              {/* Manual Build Option */}
              <div 
                onClick={addModule}
                className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700 cursor-pointer hover:border-teal-500/50 transition-colors"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-teal-500/20">
                    <Wand2 className="h-5 w-5 text-teal-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Build Manually</h4>
                    <p className="text-xs text-zinc-400">Create content step by step</p>
                  </div>
                </div>
                <p className="text-xs text-zinc-500">
                  Add modules, sections, text, videos, documents, and quizzes manually.
                </p>
              </div>
            </div>
          )}

          {/* AI Generated Badge */}
          {activity.ai_generated && (
            <div className="flex items-center gap-2 p-3 bg-purple-500/10 rounded-lg border border-purple-500/30 mb-4">
              <Zap className="h-4 w-4 text-purple-400" />
              <span className="text-sm text-purple-300">
                AI-generated course linked: <span className="font-mono text-xs">{activity.ai_course_id}</span>
              </span>
              <Badge className="bg-purple-500/20 text-purple-400 ml-auto">AI Generated</Badge>
            </div>
          )}

          {/* Modules */}
          {modules.length === 0 && !activity.ai_generated ? null : modules.length === 0 ? (
            <div className="text-center py-8 bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700">
              <FileText className="h-8 w-8 text-zinc-600 mx-auto mb-2" />
              <p className="text-sm text-zinc-500 mb-3">No manual modules. Content is AI-generated.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {modules.map((module, moduleIndex) => (
                <Card key={module.module_id} className="bg-zinc-800/50 border-zinc-700">
                  <CardHeader className="py-3 px-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <GripVertical className="h-4 w-4 text-zinc-600" />
                        <Input
                          value={module.title}
                          onChange={(e) => updateModule(module.module_id, 'title', e.target.value)}
                          placeholder="Module title"
                          className="bg-zinc-900 border-zinc-700 text-white h-8 text-sm max-w-xs"
                        />
                        <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-400">
                          {module.sections?.length || 0} sections
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => addSection(module.module_id)}
                          className="text-teal-400 hover:text-teal-300 h-8"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Section
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteModule(module.module_id)}
                          className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>

                  {/* Sections */}
                  <CardContent className="pt-0 pb-4 px-4">
                    {(!module.sections || module.sections.length === 0) ? (
                      <div className="text-center py-4 text-sm text-zinc-500">
                        No sections yet. Click &quot;+ Section&quot; to add content.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {module.sections.map((section, sectionIndex) => (
                          <div 
                            key={section.section_id}
                            className="p-3 bg-zinc-900/50 rounded-lg border border-zinc-700"
                          >
                            {/* Section Header */}
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2 flex-1">
                                <Input
                                  value={section.title}
                                  onChange={(e) => updateSection(module.module_id, section.section_id, 'title', e.target.value)}
                                  placeholder="Section title"
                                  className="bg-zinc-800 border-zinc-700 text-white h-7 text-sm max-w-[200px]"
                                />
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setShowAddContent(`${module.module_id}_${section.section_id}`)}
                                  className="text-amber-400 hover:text-amber-300 h-7 text-xs"
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Content
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteSection(module.module_id, section.section_id)}
                                  className="text-red-400 hover:text-red-300 h-7 w-7 p-0"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>

                            {/* Content Blocks */}
                            {section.content_blocks && section.content_blocks.length > 0 ? (
                              <div className="space-y-2">
                                {section.content_blocks.map((block) => (
                                  <div 
                                    key={block.block_id}
                                    className="p-3 bg-zinc-800 rounded border border-zinc-700"
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        {getContentIcon(block.type)}
                                        <span className="text-xs text-zinc-400 uppercase">{block.type}</span>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => deleteContentBlock(module.module_id, section.section_id, block.block_id)}
                                        className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </div>
                                    {renderContentBlock(block, module.module_id, section.section_id)}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-3 text-xs text-zinc-500 border border-dashed border-zinc-700 rounded">
                                Click &quot;+ Content&quot; to add text, video, documents, or quizzes
                              </div>
                            )}

                            {/* Add Content Dialog */}
                            {showAddContent === `${module.module_id}_${section.section_id}` && (
                              <div className="mt-3 p-3 bg-zinc-800 rounded-lg border border-zinc-600">
                                <p className="text-xs text-zinc-400 mb-2">Select content type:</p>
                                <div className="grid grid-cols-3 gap-2">
                                  {CONTENT_TYPES.map((type) => (
                                    <Button
                                      key={type.value}
                                      variant="outline"
                                      size="sm"
                                      onClick={() => addContentBlock(module.module_id, section.section_id, type.value)}
                                      className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 justify-start h-8 text-xs"
                                    >
                                      <type.icon className="h-3 w-3 mr-1" />
                                      {type.label}
                                    </Button>
                                  ))}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setShowAddContent(null)}
                                  className="w-full mt-2 text-zinc-500 text-xs"
                                >
                                  Cancel
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}

              {/* Add Module Button */}
              <Button
                onClick={addModule}
                variant="outline"
                className="w-full border-dashed border-zinc-700 text-zinc-400 hover:bg-zinc-800"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Module
              </Button>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>

      {/* AI Generation Dialog */}
      <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-400" />
              Generate Content with NAVI AI
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Choose content type and configure generation options
            </DialogDescription>
          </DialogHeader>

          {/* Progress Indicator - shown during generation */}
          {aiGenerating && (
            <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-purple-300">{generationMessage}</span>
                <span className="text-sm font-medium text-purple-400">{generationProgress}%</span>
              </div>
              <Progress value={generationProgress} className="h-2" />
              <p className="text-xs text-zinc-500">This may take a moment. Please do not close this dialog.</p>
            </div>
          )}

          {!aiGenerating && (
            <div className="space-y-4 py-4">
              {/* Activity Info */}
              <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <p className="text-sm text-zinc-300">
                  <strong>Activity:</strong> {activity.title || 'Untitled Activity'}
                </p>
              </div>

              {/* Content Type Selection */}
              <div>
                <Label className="text-zinc-400 mb-2 block">Content Type</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div 
                    onClick={() => setAiForm({ ...aiForm, content_type: 'course' })}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      aiForm.content_type === 'course' 
                        ? 'bg-purple-500/20 border-purple-500' 
                        : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <BookOpen className={`h-5 w-5 ${aiForm.content_type === 'course' ? 'text-purple-400' : 'text-zinc-400'}`} />
                      <div>
                        <p className={`font-medium ${aiForm.content_type === 'course' ? 'text-purple-300' : 'text-white'}`}>
                          AI Course
                        </p>
                        <p className="text-xs text-zinc-500">Structured modules & quizzes</p>
                      </div>
                    </div>
                  </div>
                  <div 
                    onClick={() => setAiForm({ ...aiForm, content_type: 'learning_lab' })}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      aiForm.content_type === 'learning_lab' 
                        ? 'bg-teal-500/20 border-teal-500' 
                        : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <FlaskConical className={`h-5 w-5 ${aiForm.content_type === 'learning_lab' ? 'text-teal-400' : 'text-zinc-400'}`} />
                      <div>
                        <p className={`font-medium ${aiForm.content_type === 'learning_lab' ? 'text-teal-300' : 'text-white'}`}>
                          Learning Lab
                        </p>
                        <p className="text-xs text-zinc-500">Interactive scenarios & tasks</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-zinc-400">Overview / Instructions (optional)</Label>
                <Textarea
                  value={aiForm.overview}
                  onChange={(e) => setAiForm({ ...aiForm, overview: e.target.value })}
                  placeholder={aiForm.content_type === 'learning_lab' 
                    ? "Describe the scenario context, skills to develop..."
                    : "Describe what this course should cover..."
                  }
                  rows={3}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">Duration</Label>
                  <Select
                    value={aiForm.duration}
                    onValueChange={(value) => setAiForm({ ...aiForm, duration: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {DURATION_OPTIONS.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-zinc-400">
                    {aiForm.content_type === 'learning_lab' ? 'Number of Scenarios' : 'Number of Sections'}
                  </Label>
                  <Select
                    value={aiForm.num_sections.toString()}
                    onValueChange={(value) => setAiForm({ ...aiForm, num_sections: parseInt(value) })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {NUM_SECTIONS_OPTIONS.map((n) => (
                        <SelectItem key={n} value={n.toString()}>
                          {n} {aiForm.content_type === 'learning_lab' ? 'scenarios' : 'sections'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAIDialog(false)}
              className="border-zinc-700 text-zinc-300"
              disabled={aiGenerating}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleGeneratePreview}
              disabled={previewLoading || aiGenerating || !activity.title}
              className="border-purple-700 text-purple-300 hover:bg-purple-500/10"
            >
              {previewLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Loading Preview...
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Preview Outline
                </>
              )}
            </Button>
            <Button
              onClick={handleAIGenerate}
              disabled={aiGenerating || !activity.title}
              className={aiForm.content_type === 'learning_lab' 
                ? "bg-teal-600 hover:bg-teal-700 text-white"
                : "bg-purple-600 hover:bg-purple-700 text-white"
              }
            >
              {aiGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate {aiForm.content_type === 'learning_lab' ? 'Lab' : 'Course'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-amber-400" />
              Content Preview
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Review the AI-generated outline before creating
            </DialogDescription>
          </DialogHeader>

          {previewData && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <h3 className="text-lg font-semibold text-white mb-2">{previewData.title}</h3>
                <p className="text-sm text-zinc-400">{previewData.overview}</p>
                {previewData.duration && (
                  <Badge className="mt-2 bg-zinc-700 text-zinc-300">{previewData.duration}</Badge>
                )}
              </div>

              {/* Sections/Scenarios */}
              {previewData.sections && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                    <BookOpen className="h-4 w-4" /> Sections
                  </h4>
                  {previewData.sections.map((section, idx) => (
                    <div key={idx} className="p-3 bg-zinc-800 rounded-lg border border-zinc-700">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-teal-400" />
                        <span className="text-white font-medium">{section.title}</span>
                      </div>
                      {section.description && (
                        <p className="text-xs text-zinc-400 mt-1 ml-6">{section.description}</p>
                      )}
                      {section.topics && (
                        <div className="flex flex-wrap gap-1 mt-2 ml-6">
                          {section.topics.map((topic, i) => (
                            <Badge key={i} variant="outline" className="text-xs border-zinc-600 text-zinc-400">
                              {topic}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Scenarios for Learning Labs */}
              {previewData.scenarios && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                    <FlaskConical className="h-4 w-4" /> Scenarios
                  </h4>
                  {previewData.scenarios.map((scenario, idx) => (
                    <div key={idx} className="p-3 bg-zinc-800 rounded-lg border border-zinc-700">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-teal-400" />
                        <span className="text-white font-medium">{scenario.title}</span>
                      </div>
                      {scenario.brief && (
                        <p className="text-xs text-zinc-400 mt-1 ml-6">{scenario.brief}</p>
                      )}
                      {scenario.challenge_type && (
                        <Badge variant="outline" className="text-xs border-teal-600 text-teal-400 mt-2 ml-6">
                          {scenario.challenge_type}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Learning Outcomes */}
              {previewData.learning_outcomes && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-zinc-300">Learning Outcomes</h4>
                  <ul className="space-y-1">
                    {previewData.learning_outcomes.map((outcome, idx) => (
                      <li key={idx} className="text-sm text-zinc-400 flex items-start gap-2">
                        <CheckCircle className="h-3 w-3 text-green-400 mt-1 flex-shrink-0" />
                        {outcome}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Skills for Labs */}
              {previewData.skills_developed && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-zinc-300">Skills Developed</h4>
                  <div className="flex flex-wrap gap-2">
                    {previewData.skills_developed.map((skill, idx) => (
                      <Badge key={idx} className="bg-teal-500/20 text-teal-400">{skill}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPreview(false)}
              className="border-zinc-700 text-zinc-300"
            >
              Close Preview
            </Button>
            <Button
              onClick={() => {
                setShowPreview(false);
                handleAIGenerate();
              }}
              className={aiForm.content_type === 'learning_lab' 
                ? "bg-teal-600 hover:bg-teal-700 text-white"
                : "bg-purple-600 hover:bg-purple-700 text-white"
              }
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Generate Full Content
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
